import { useCallback, useEffect, useRef, useState } from "react";
import {
  ArrowLongRightIcon,
  ArrowUturnLeftIcon,
  ArrowUturnRightIcon,
  PencilIcon,
  TrashIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { MARKUP_COLORS } from "./drawingViewerConstants";

const CANVAS_W = 520;
const CANVAS_H = 390;
const TEXT_FONT = "600 18px Inter, system-ui, sans-serif";
const TEXT_HIT_PAD = 8;

const CircleIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const RectIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="4" y="4" width="16" height="16" rx="1.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const TextIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <rect x="4" y="5" width="16" height="14" rx="1.5" stroke="currentColor" strokeWidth="2" />
  </svg>
);

const TextIconBtn = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 18V7.5A1.5 1.5 0 0 1 5.5 6H14l4 4v8A1.5 1.5 0 0 1 16.5 18H5.5A1.5 1.5 0 0 1 4 16.5"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
    <path d="M14 6v4h4" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    <path d="M7.5 14h7M7.5 11h4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
  </svg>
);

const CursorIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M4 3l7.5 17 2-6.5L20 11.5 4 3z"
      stroke="currentColor"
      strokeWidth="1.8"
      strokeLinejoin="round"
    />
  </svg>
);

const measureTextBox = (ctx, stroke) => {
  const origin = stroke.points?.[0] || { x: 16, y: 24 };
  ctx.font = TEXT_FONT;
  const metrics = ctx.measureText(stroke.text || "");
  const width = Math.max(metrics.width, 12);
  const ascent = metrics.actualBoundingBoxAscent || 14;
  const descent = metrics.actualBoundingBoxDescent || 4;
  return {
    x: origin.x - TEXT_HIT_PAD,
    y: origin.y - ascent - TEXT_HIT_PAD,
    w: width + TEXT_HIT_PAD * 2,
    h: ascent + descent + TEXT_HIT_PAD * 2,
    origin,
  };
};

const paintStroke = (ctx, stroke, { selected = false } = {}) => {
  if (!stroke) return;
  const tool = stroke.tool;
  const pts = stroke.points || [];
  ctx.strokeStyle = stroke.color || "#e5484d";
  ctx.fillStyle = stroke.color || "#e5484d";
  ctx.lineWidth = tool === "arrow" ? 3 : 2.5;
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (tool === "text" && stroke.text) {
    const origin = pts[0] || { x: 16, y: 24 };
    ctx.font = TEXT_FONT;
    ctx.fillText(stroke.text, origin.x, origin.y);
    if (selected) {
      const box = measureTextBox(ctx, stroke);
      ctx.save();
      ctx.strokeStyle = "#2b6ef0";
      ctx.lineWidth = 1.5;
      ctx.setLineDash([4, 3]);
      ctx.strokeRect(box.x, box.y, box.w, box.h);
      ctx.restore();
    }
    return;
  }

  if (!pts.length) return;

  if (tool === "circle" && pts.length >= 2) {
    const a = pts[0];
    const b = pts[pts.length - 1];
    const cx = (a.x + b.x) / 2;
    const cy = (a.y + b.y) / 2;
    const rx = Math.abs(b.x - a.x) / 2;
    const ry = Math.abs(b.y - a.y) / 2;
    if (rx < 1 && ry < 1) return;
    ctx.beginPath();
    ctx.ellipse(cx, cy, Math.max(rx, 1), Math.max(ry, 1), 0, 0, Math.PI * 2);
    ctx.stroke();
    return;
  }

  if (tool === "rectangular" && pts.length >= 2) {
    const a = pts[0];
    const b = pts[pts.length - 1];
    const x = Math.min(a.x, b.x);
    const y = Math.min(a.y, b.y);
    const w = Math.abs(b.x - a.x);
    const h = Math.abs(b.y - a.y);
    if (w < 1 && h < 1) return;
    ctx.strokeRect(x, y, Math.max(w, 1), Math.max(h, 1));
    return;
  }

  ctx.beginPath();
  pts.forEach((pt, index) => {
    if (index === 0) ctx.moveTo(pt.x, pt.y);
    else ctx.lineTo(pt.x, pt.y);
  });
  ctx.stroke();

  if (tool === "arrow" && pts.length >= 2) {
    const a = pts[pts.length - 2];
    const b = pts[pts.length - 1];
    const angle = Math.atan2(b.y - a.y, b.x - a.x);
    const head = 12;
    ctx.beginPath();
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(
      b.x - head * Math.cos(angle - Math.PI / 6),
      b.y - head * Math.sin(angle - Math.PI / 6)
    );
    ctx.moveTo(b.x, b.y);
    ctx.lineTo(
      b.x - head * Math.cos(angle + Math.PI / 6),
      b.y - head * Math.sin(angle + Math.PI / 6)
    );
    ctx.stroke();
  }
};

const paintAll = (ctx, marks, draft, selectedIndex) => {
  ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
  marks.forEach((stroke, index) =>
    paintStroke(ctx, stroke, { selected: index === selectedIndex })
  );
  if (draft) paintStroke(ctx, draft);
};

/**
 * Overlay for sketching on a snapshot crop before pin save.
 * Coordinate space is fixed 520×390 and matches the displayed stage 1:1.
 */
export const DrawingObservationSketchOverlay = ({
  t,
  open,
  backgroundUrl,
  onComplete,
  onSkip,
  onCancel,
}) => {
  const canvasRef = useRef(null);
  const stageRef = useRef(null);
  const draftRef = useRef(null);
  const drawingRef = useRef(false);
  const marksRef = useRef([]);
  const selectedIndexRef = useRef(-1);
  const dragTextRef = useRef(null);
  const lastTapRef = useRef({ index: -1, at: 0 });
  const [tool, setTool] = useState("pen");
  const [color, setColor] = useState(MARKUP_COLORS[0].val);
  const [past, setPast] = useState([]);
  const [marks, setMarks] = useState([]);
  const [future, setFuture] = useState([]);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [, setPaintTick] = useState(0);

  const repaint = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    paintAll(ctx, marksRef.current, draftRef.current, selectedIndexRef.current);
  }, []);

  const commitMarks = useCallback(
    (nextMarks, nextSelected = -1) => {
      setPast((prev) => [...prev, marksRef.current]);
      marksRef.current = nextMarks;
      setMarks(nextMarks);
      setFuture([]);
      selectedIndexRef.current = nextSelected;
      setSelectedIndex(nextSelected);
      requestAnimationFrame(repaint);
    },
    [repaint]
  );

  // Reset session only when the overlay closes — marks persist while open.
  useEffect(() => {
    if (!open) {
      marksRef.current = [];
      draftRef.current = null;
      drawingRef.current = false;
      dragTextRef.current = null;
      selectedIndexRef.current = -1;
      setMarks([]);
      setPast([]);
      setFuture([]);
      setSelectedIndex(-1);
      setTool("pen");
      setColor(MARKUP_COLORS[0].val);
      return;
    }
    requestAnimationFrame(repaint);
  }, [open, repaint]);

  useEffect(() => {
    marksRef.current = marks;
    selectedIndexRef.current = selectedIndex;
    repaint();
  }, [marks, selectedIndex, repaint]);

  if (!open) return null;

  const pointerPos = (event) => {
    const stage = stageRef.current;
    if (!stage) return { x: 0, y: 0 };
    const rect = stage.getBoundingClientRect();
    if (!rect.width || !rect.height) return { x: 0, y: 0 };
    return {
      x: ((event.clientX - rect.left) / rect.width) * CANVAS_W,
      y: ((event.clientY - rect.top) / rect.height) * CANVAS_H,
    };
  };

  const bumpPaint = () => {
    repaint();
    setPaintTick((n) => n + 1);
  };

  const hitTestText = (pt) => {
    const canvas = canvasRef.current;
    if (!canvas) return -1;
    const ctx = canvas.getContext("2d");
    if (!ctx) return -1;
    for (let i = marksRef.current.length - 1; i >= 0; i -= 1) {
      const stroke = marksRef.current[i];
      if (stroke?.tool !== "text" || !stroke.text) continue;
      const box = measureTextBox(ctx, stroke);
      if (
        pt.x >= box.x &&
        pt.x <= box.x + box.w &&
        pt.y >= box.y &&
        pt.y <= box.y + box.h
      ) {
        return i;
      }
    }
    return -1;
  };

  const promptText = (initial = "") => {
    const text = window.prompt(
      t("drawings.observation.sketch.text_prompt"),
      initial
    );
    return text?.trim() || null;
  };

  const editTextAt = (index) => {
    const stroke = marksRef.current[index];
    if (!stroke || stroke.tool !== "text") return;
    const nextText = promptText(stroke.text || "");
    if (nextText == null) return;
    if (nextText === stroke.text) {
      selectedIndexRef.current = index;
      setSelectedIndex(index);
      bumpPaint();
      return;
    }
    const next = marksRef.current.map((item, i) =>
      i === index ? { ...item, text: nextText } : item
    );
    commitMarks(next, index);
  };

  const handlePointerDown = (event) => {
    event.preventDefault();
    event.currentTarget?.setPointerCapture?.(event.pointerId);
    const pt = pointerPos(event);

    // Select / move / double-click edit for existing text marks.
    if (tool === "select" || tool === "text") {
      const hit = hitTestText(pt);
      if (hit >= 0) {
        const now = Date.now();
        const isDouble =
          lastTapRef.current.index === hit && now - lastTapRef.current.at < 350;
        lastTapRef.current = { index: hit, at: now };

        selectedIndexRef.current = hit;
        setSelectedIndex(hit);
        bumpPaint();

        if (isDouble) {
          editTextAt(hit);
          return;
        }

        const origin = marksRef.current[hit].points?.[0] || pt;
        dragTextRef.current = {
          index: hit,
          offsetX: pt.x - origin.x,
          offsetY: pt.y - origin.y,
          startMarks: marksRef.current,
          moved: false,
        };
        return;
      }
    }

    if (tool === "text") {
      const text = promptText("");
      if (text) {
        commitMarks(
          [
            ...marksRef.current,
            { tool: "text", color, points: [pt], text },
          ],
          marksRef.current.length
        );
      }
      return;
    }

    if (tool === "select") {
      selectedIndexRef.current = -1;
      setSelectedIndex(-1);
      bumpPaint();
      return;
    }

    selectedIndexRef.current = -1;
    setSelectedIndex(-1);
    drawingRef.current = true;
    draftRef.current = { tool, color, points: [pt] };
    bumpPaint();
  };

  const handlePointerMove = (event) => {
    if (dragTextRef.current) {
      const pt = pointerPos(event);
      const { index, offsetX, offsetY, startMarks } = dragTextRef.current;
      const nextOrigin = {
        x: Math.min(CANVAS_W - 8, Math.max(8, pt.x - offsetX)),
        y: Math.min(CANVAS_H - 8, Math.max(16, pt.y - offsetY)),
      };
      dragTextRef.current.moved = true;
      const next = startMarks.map((item, i) =>
        i === index ? { ...item, points: [nextOrigin] } : item
      );
      marksRef.current = next;
      setMarks(next);
      bumpPaint();
      return;
    }

    if (!drawingRef.current || !draftRef.current) return;
    const pt = pointerPos(event);
    const currentTool = draftRef.current.tool;
    if (
      currentTool === "arrow" ||
      currentTool === "circle" ||
      currentTool === "rectangular"
    ) {
      draftRef.current = {
        ...draftRef.current,
        points: [draftRef.current.points[0], pt],
      };
    } else {
      draftRef.current = {
        ...draftRef.current,
        points: [...draftRef.current.points, pt],
      };
    }
    bumpPaint();
  };

  const handlePointerUp = () => {
    if (dragTextRef.current) {
      const { moved, startMarks } = dragTextRef.current;
      const index = dragTextRef.current.index;
      dragTextRef.current = null;
      if (moved) {
        // Commit move onto undo stack from the pre-drag snapshot.
        setPast((prev) => [...prev, startMarks]);
        setFuture([]);
        selectedIndexRef.current = index;
        setSelectedIndex(index);
      }
      bumpPaint();
      return;
    }

    if (!drawingRef.current) return;
    drawingRef.current = false;
    const draft = draftRef.current;
    draftRef.current = null;
    if (draft?.points?.length > 1) {
      commitMarks([...marksRef.current, draft]);
    } else {
      bumpPaint();
    }
  };

  const handleDoubleClick = (event) => {
    event.preventDefault();
    const pt = pointerPos(event);
    const hit = hitTestText(pt);
    if (hit >= 0) editTextAt(hit);
  };

  const undo = () => {
    if (!past.length) return;
    const previous = past[past.length - 1];
    setFuture((f) => [marksRef.current, ...f]);
    setPast((p) => p.slice(0, -1));
    marksRef.current = previous;
    setMarks(previous);
    selectedIndexRef.current = -1;
    setSelectedIndex(-1);
    requestAnimationFrame(repaint);
  };

  const redo = () => {
    if (!future.length) return;
    const next = future[0];
    setPast((p) => [...p, marksRef.current]);
    setFuture((f) => f.slice(1));
    marksRef.current = next;
    setMarks(next);
    selectedIndexRef.current = -1;
    setSelectedIndex(-1);
    requestAnimationFrame(repaint);
  };

  const clearAll = () => {
    if (!marksRef.current.length) return;
    commitMarks([]);
  };

  const exportComposite = async () => {
    const canvas = document.createElement("canvas");
    canvas.width = CANVAS_W;
    canvas.height = CANVAS_H;
    const ctx = canvas.getContext("2d");
    if (!ctx) return { blob: null, strokes: marksRef.current };

    if (backgroundUrl) {
      await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          ctx.drawImage(img, 0, 0, CANVAS_W, CANVAS_H);
          resolve();
        };
        img.onerror = () => resolve();
        img.src = backgroundUrl;
      });
    } else {
      ctx.fillStyle = "#f8fafc";
      ctx.fillRect(0, 0, CANVAS_W, CANVAS_H);
    }

    marksRef.current.forEach((stroke) => paintStroke(ctx, stroke));

    const blob = await new Promise((resolve) =>
      canvas.toBlob((b) => resolve(b), "image/jpeg", 0.92)
    );
    return { blob, strokes: marksRef.current };
  };

  const toolBtn = (id, active, onClick, icon, label) => (
    <button
      key={id}
      type="button"
      onClick={onClick}
      className={`inline-flex h-9 items-center gap-1.5 rounded-[9px] border px-2.5 text-[12.5px] font-semibold ${
        active
          ? "border-[#2b6ef0] bg-[#eaf1fe] text-[#1d4fb0]"
          : "border-[#e5e8ec] text-[#59616d]"
      }`}
      title={label}
    >
      {icon}
      <span className="hidden sm:inline">{label}</span>
    </button>
  );

  return (
    <div className="fixed inset-0 z-[140] flex items-stretch justify-center bg-[rgba(15,23,42,0.55)] p-0 sm:items-center sm:p-4">
      <div className="flex h-[100dvh] max-h-[100dvh] w-full max-w-lg flex-col overflow-hidden rounded-none bg-white shadow-xl sm:h-auto sm:max-h-[calc(100dvh-32px)] sm:rounded-[16px]">
        <div className="flex flex-shrink-0 items-center justify-between border-b border-[#e5e8ec] px-3 py-2 sm:px-3.5 sm:py-3">
          <div>
            <div className="text-[15px] font-semibold text-[#14171c]">
              {t("drawings.observation.sketch.title")}
            </div>
            <div className="text-[12px] text-[#59616d]">
              {t("drawings.observation.sketch.help")}
            </div>
          </div>
          <button
            type="button"
            onClick={onCancel}
            className="grid h-9 w-9 place-items-center rounded-md text-[#59616d] hover:bg-[#eef0f3]"
          >
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        <div className="relative mx-3 mt-2 flex min-h-0 flex-1 items-center justify-center overflow-hidden rounded-[12px] border border-[#e5e8ec] bg-[#f6f7f9] sm:mx-3.5 sm:mt-3 sm:flex-none">
          <div
            ref={stageRef}
            className="relative w-full max-h-full"
            style={{ aspectRatio: `${CANVAS_W} / ${CANVAS_H}` }}
          >
            {backgroundUrl && (
              <img
                src={backgroundUrl}
                alt=""
                draggable={false}
                className="pointer-events-none absolute inset-0 h-full w-full"
                style={{ objectFit: "fill" }}
              />
            )}
            <canvas
              ref={canvasRef}
              width={CANVAS_W}
              height={CANVAS_H}
              className="absolute inset-0 z-[1] block h-full w-full touch-none"
              onPointerDown={handlePointerDown}
              onPointerMove={handlePointerMove}
              onPointerUp={handlePointerUp}
              onPointerCancel={handlePointerUp}
              onDoubleClick={handleDoubleClick}
            />
          </div>
        </div>

        <div className="flex flex-shrink-0 flex-wrap items-center gap-1.5 px-3 py-2 sm:gap-2 sm:px-3.5 sm:py-3">
          {toolBtn(
            "select",
            tool === "select",
            () => setTool("select"),
            <CursorIcon className="h-4 w-4" />,
            t("drawings.observation.sketch.select")
          )}
          {toolBtn(
            "pen",
            tool === "pen",
            () => setTool("pen"),
            <PencilIcon className="h-4 w-4" />,
            t("drawings.observation.sketch.pen")
          )}
          {toolBtn(
            "arrow",
            tool === "arrow",
            () => setTool("arrow"),
            <ArrowLongRightIcon className="h-4 w-4" />,
            t("drawings.observation.sketch.arrow")
          )}
          {toolBtn(
            "circle",
            tool === "circle",
            () => setTool("circle"),
            <CircleIcon className="h-4 w-4" />,
            t("drawings.observation.sketch.circle")
          )}
          {toolBtn(
            "rectangular",
            tool === "rectangular",
            () => setTool("rectangular"),
            <RectIcon className="h-4 w-4" />,
            t("drawings.observation.sketch.rectangular")
          )}
          {toolBtn(
            "text",
            tool === "text",
            () => setTool("text"),
            <TextIconBtn className="h-4 w-4" />,
            t("drawings.observation.sketch.text")
          )}
          <div className="flex gap-1.5">
            {MARKUP_COLORS.slice(0, 5).map((swatch) => (
              <button
                key={swatch.id}
                type="button"
                onClick={() => setColor(swatch.val)}
                className={`h-6 w-6 rounded-full border-2 border-white ${
                  color === swatch.val
                    ? "shadow-[0_0_0_2px_#fff,0_0_0_4px_#2b6ef0]"
                    : "shadow-[0_0_0_1px_#d6dae0]"
                }`}
                style={{ background: swatch.val }}
              />
            ))}
          </div>
          <button
            type="button"
            onClick={undo}
            disabled={!past.length}
            className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] border border-[#e5e8ec] text-[#59616d] disabled:opacity-40"
            title={t("drawings.observation.sketch.undo")}
          >
            <ArrowUturnLeftIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={redo}
            disabled={!future.length}
            className="inline-flex h-9 w-9 items-center justify-center rounded-[9px] border border-[#e5e8ec] text-[#59616d] disabled:opacity-40"
            title={t("drawings.observation.sketch.redo")}
          >
            <ArrowUturnRightIcon className="h-4 w-4" />
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="ml-auto inline-flex h-9 items-center gap-1 rounded-[9px] border border-[#e5e8ec] px-2.5 text-[12.5px] font-semibold text-[#59616d]"
          >
            <TrashIcon className="h-4 w-4" />
            {t("drawings.observation.sketch.clear")}
          </button>
        </div>

        <div className="sticky bottom-0 flex flex-shrink-0 gap-2 border-t border-[#e5e8ec] bg-white px-3 py-2.5 pb-[calc(0.65rem+env(safe-area-inset-bottom,0px))] sm:px-3.5 sm:py-3 sm:pb-[calc(0.75rem+env(safe-area-inset-bottom,0px))]">
          <button
            type="button"
            onClick={onSkip}
            className="h-10 flex-1 rounded-[10px] border border-[#e5e8ec] text-[13px] font-semibold text-[#14171c]"
          >
            {t("drawings.observation.sketch.skip")}
          </button>
          <button
            type="button"
            onClick={async () => {
              const result = await exportComposite();
              onComplete(result);
            }}
            className="h-10 flex-[1.4] rounded-[10px] bg-[#2b6ef0] text-[13px] font-semibold text-white"
          >
            {t("drawings.observation.sketch.done")}
          </button>
        </div>
      </div>
    </div>
  );
};
